import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FaBell, FaUserEdit, FaTimes, FaExclamationTriangle, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { getList, UpdateRecord, SuccessAlert, ErrorAlert, URLPRIFIX } from "../../../functions/api";
import { getlocal } from "../../../functions/localstorage";
import { useStore } from "../../../Components/StateProvider";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

// ─── helpers ─────────────────────────────────────────────────────────────────
const formatDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

const priorityClass = (p) => {
  switch ((p || "").toUpperCase()) {
    case "HIGH": return "bg-red-100 text-red-700";
    case "MEDIUM": return "bg-yellow-100 text-yellow-700";
    case "LOW": return "bg-green-100 text-green-700";
    default: return "bg-gray-100 text-gray-600";
  }
};

const MODULE_TYPE = 13; // Trademark

// ─── component ───────────────────────────────────────────────────────────────
const OverdueTasks = () => {
  const navigate = useNavigate();
  const store = useStore();
  const user = getlocal("system")?.user || {};

  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);

  // dropdown option lists (derived from data)
  const [projectOpts, setProjectOpts] = useState([]);
  const [userOpts, setUserOpts] = useState([]);

  // filter state
  const [search, setSearch] = useState("");
  const [projectFilter, setProjectFilter] = useState("All Projects");
  const [userFilter, setUserFilter] = useState("All Users");
  const [priorityFilter, setPriorityFilter] = useState("All Priorities");
  const [daysFilter, setDaysFilter] = useState("All");

  // sort state
  const [sortField, setSortField] = useState("");
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"

  // pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // users list for reassign
  const [allUsers, setAllUsers] = useState([]);

  // notify modal
  const [notifyModal, setNotifyModal] = useState(null);
  const [notifying, setNotifying] = useState(false);

  // reassign modal
  const [reassignModal, setReassignModal] = useState(null);
  const [selectedUser, setSelectedUser] = useState("");
  const [reassigning, setReassigning] = useState(false);

  // ── fetch overdue tasks ──────────────────────────────────────────────────
  const fetchOverdueTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getList({
        listUrl: `/subtask/getoverduetasks/${MODULE_TYPE}`,
        field: "",
        search: "",
        sort: "asc",
        page: 1,
        count: 500,
      });
      const list = res?.data?.List || res?.data?.list || [];
      setAllData(list);

      const projects = [...new Set(list.map(t => t.project_name).filter(Boolean))].sort();
      const users = [...new Set(list.map(t => t.assigned_to_name || t.assigned_to_username).filter(Boolean))].sort();
      setProjectOpts(projects);
      setUserOpts(users);
    } catch (e) {
      ErrorAlert("Failed to load overdue tasks.");
    } finally {
      setLoading(false);
    }
  }, []);

  // ── fetch users list for reassign ────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    try {
      const res = await getList({
        listUrl: "/users/getusers",
        field: "userName",
        sort: "asc",
        page: 1,
        count: 500,
      });
      const list = res?.data?.list || [];
      setAllUsers(list.map(u => ({
        value: u.id,
        label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.userName,
      })));
    } catch (_) { }
  }, []);

  useEffect(() => {
    store.setmodule("trademark_task");
    fetchOverdueTasks();
    fetchUsers();
  }, []);

  // ── client-side filtering ────────────────────────────────────────────────
  useEffect(() => {
    let filtered = allData.filter((task) => {
      const taskName = (task.subtask_title || "").toLowerCase();
      const projName = (task.project_name || "").toLowerCase();
      const assignee = (task.assigned_to_name || task.assigned_to_username || "").toLowerCase();
      const q = search.toLowerCase();

      const matchSearch = !q || taskName.includes(q) || projName.includes(q) || assignee.includes(q);
      const matchProject = projectFilter === "All Projects" || task.project_name === projectFilter;
      const matchUser = userFilter === "All Users" ||
        (task.assigned_to_name || task.assigned_to_username) === userFilter;
      const matchPriority = priorityFilter === "All Priorities" ||
        (task.priority || "").toUpperCase() === priorityFilter;

      let matchDays = true;
      if (daysFilter === "> 5 Days") matchDays = task.days_overdue > 5;
      if (daysFilter === "1-5 Days") matchDays = task.days_overdue >= 1 && task.days_overdue <= 5;

      return matchSearch && matchProject && matchUser && matchPriority && matchDays;
    });
    setFilteredData(filtered);
    setCurrentPage(1); // reset to first page on filter change
  }, [search, projectFilter, userFilter, priorityFilter, daysFilter, allData]);

  // ── client-side sorting ──────────────────────────────────────────────────
  const sortedData = useMemo(() => {
    if (!sortField) return filteredData;
    return [...filteredData].sort((a, b) => {
      let aVal = a[sortField] ?? "";
      let bVal = b[sortField] ?? "";
      if (sortField === "days_overdue") {
        aVal = Number(aVal); bVal = Number(bVal);
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      if (["due_date", "assigned_to_date", "created_date", "work_start_date"].includes(sortField)) {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortField, sortDir]);

  // ── pagination ───────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const pagedData = sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <FaSort className="inline ml-1 opacity-30" size={10} />;
    return sortDir === "asc"
      ? <FaSortUp className="inline ml-1 text-blue-500" size={10} />
      : <FaSortDown className="inline ml-1 text-blue-500" size={10} />;
  };

  // ── notify handler ───────────────────────────────────────────────────────
  const handleNotify = async () => {
    if (!notifyModal) return;
    if (!notifyModal.assigned_to) {
      ErrorAlert("This task has no assigned user.");
      return;
    }
    setNotifying(true);
    try {
      const res = await UpdateRecord(
        `/subtask/sendreminder/${notifyModal.id}/${user.id}/${notifyModal.assigned_to}`
      );
      if (res.success) {
        SuccessAlert(`Reminder sent to ${notifyModal.assigned_to_name || "assigned user"}.`);
        setNotifyModal(null);
      } else {
        ErrorAlert("Failed to send reminder.");
      }
    } catch (_) {
      ErrorAlert("Failed to send reminder.");
    } finally {
      setNotifying(false);
    }
  };

  // ── reassign handler ─────────────────────────────────────────────────────
  const handleReassign = async () => {
    if (!reassignModal || !selectedUser) {
      ErrorAlert("Please select a user to reassign.");
      return;
    }
    setReassigning(true);
    try {
      const res = await UpdateRecord(
        `/subtask/reassigntask/${reassignModal.id}/${selectedUser}/${user.id}`
      );
      if (res.success) {
        SuccessAlert("Task reassigned successfully.");
        setReassignModal(null);
        setSelectedUser("");
        fetchOverdueTasks();
      } else {
        ErrorAlert("Failed to reassign task.");
      }
    } catch (_) {
      ErrorAlert("Failed to reassign task.");
    } finally {
      setReassigning(false);
    }
  };

  // ── render ───────────────────────────────────────────────────────────────
  return (
    <div className="w-full h-full p-4 overflow-y-auto bg-gray-50">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Overdue Tasks</h1>
        <button
          onClick={fetchOverdueTasks}
          className="text-sm px-3 py-1.5 bg-white border border-gray-300 rounded-md hover:bg-gray-50 text-gray-600"
        >
          ↺ Refresh
        </button>
      </div>

      {/* Alert banner */}
      {!loading && filteredData.length > 0 && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 flex items-center gap-2">
          <FaBell className="text-red-500 flex-shrink-0" />
          <span>
            <strong>Overdue Tasks Alert — </strong>
            You have <strong>{filteredData.length}</strong> overdue task{filteredData.length !== 1 ? "s" : ""} requiring immediate attention.
          </span>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-4">
        <div className="mb-3">
          <input
            type="text"
            placeholder="Search...."
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Project</label>
            <select
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none text-sm"
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
            >
              <option>All Projects</option>
              {projectOpts.map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Assigned User</label>
            <select
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none text-sm"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
            >
              <option>All Users</option>
              {userOpts.map(u => <option key={u}>{u}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Priority</label>
            <select
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none text-sm"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <option>All Priorities</option>
              <option>HIGH</option>
              <option>MEDIUM</option>
              <option>LOW</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Days Overdue</label>
            <select
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none text-sm"
              value={daysFilter}
              onChange={(e) => setDaysFilter(e.target.value)}
            >
              <option>All</option>
              <option>&gt; 5 Days</option>
              <option>1-5 Days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-800">
            Overdue Tasks ({filteredData.length})
          </h2>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">Rows per page:</label>
            <select
              className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none"
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">Loading…</div>
        ) : filteredData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-2">
            <FaExclamationTriangle size={28} />
            <p>No overdue tasks found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-600">
              <thead className="text-xs text-gray-500 uppercase bg-gray-50 border-b">
                <tr>
                  {[
                    { label: "Task Name", field: "subtask_title" },
                    { label: "Project", field: "project_name" },
                    { label: "Assigned To", field: "assigned_to_name" },
                    { label: "Assigned Date", field: "assigned_to_date" },
                    { label: "Started Date", field: "work_start_date" },
                    { label: "Priority", field: "priority" },
                    { label: "Due Date", field: "due_date" },
                    { label: "Days Overdue", field: "days_overdue" },
                  ].map(({ label, field }) => (
                    <th
                      key={field}
                      className="px-4 py-3 font-medium cursor-pointer select-none hover:bg-gray-100"
                      onClick={() => handleSort(field)}
                    >
                      {label}<SortIcon field={field} />
                    </th>
                  ))}
                  <th className="px-4 py-3 font-medium text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedData.map((task, idx) => (
                  <tr
                    key={task.id || idx}
                    className="bg-white border-b hover:bg-gray-50 cursor-pointer"
                    onClick={() =>
                      navigate(`/${URLPRIFIX}/trademark/tasks/view/${task.id}`)
                    }
                  >
                    <td className="px-4 py-3 font-medium text-gray-900 max-w-[200px] truncate">
                      {task.subtask_title || "—"}
                    </td>
                    <td className="px-4 py-3">{task.project_name || "—"}</td>
                    {/* <td className="px-4 py-3 text-blue-600 hover:underline">
                      {task.task_title ? `📄 ${task.task_title}` : "—"}
                    </td> */}
                    <td className="px-4 py-3">
                      {task.assigned_to_name || task.assigned_to_username || "Unassigned"}
                    </td>
                    <td className="px-4 py-3">{formatDate(task.assigned_to_date || task.created_date)}</td>
                    <td className="px-4 py-3">{formatDate(task.work_start_date)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${priorityClass(task.priority)}`}>
                        {(task.priority || "—").toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">{formatDate(task.due_date)}</td>
                    <td className="px-4 py-3">
                      <span className="bg-red-100 text-red-600 px-2 py-0.5 rounded text-xs font-semibold">
                        {task.days_overdue} day{task.days_overdue !== 1 ? "s" : ""}
                      </span>
                    </td>
                    <td
                      className="px-4 py-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex gap-3 justify-center">
                        <button
                          id={`notify-tm-${task.id}`}
                          onClick={() => setNotifyModal(task)}
                          className="text-gray-500 hover:text-blue-600 flex items-center gap-1 text-xs"
                          title="Send reminder"
                        >
                          <FaBell size={11} /> Notify
                        </button>
                        <button
                          id={`reassign-tm-${task.id}`}
                          onClick={() => { setReassignModal(task); setSelectedUser(""); }}
                          className="text-gray-500 hover:text-blue-600 flex items-center gap-1 text-xs"
                          title="Reassign task"
                        >
                          <FaUserEdit size={11} /> Reassign
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Pagination Controls ──────────────────────────────────────── */}
        {!loading && sortedData.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm text-gray-600">
            <span className="text-xs">
              Showing {Math.min((currentPage - 1) * pageSize + 1, sortedData.length)}–{Math.min(currentPage * pageSize, sortedData.length)} of {sortedData.length}
            </span>
            <div className="flex items-center gap-1">
              <button
                className="px-2 py-1 rounded border border-gray-300 text-xs hover:bg-gray-100 disabled:opacity-40"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >«</button>
              <button
                className="px-2 py-1 rounded border border-gray-300 text-xs hover:bg-gray-100 disabled:opacity-40"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >‹ Prev</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, i, arr) => {
                  if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === "..." ? (
                    <span key={`ellipsis-${i}`} className="px-2 text-xs text-gray-400">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`px-2 py-1 rounded border text-xs ${currentPage === p
                        ? "bg-blue-600 text-white border-blue-600"
                        : "border-gray-300 hover:bg-gray-100"
                        }`}
                    >{p}</button>
                  )
                )}
              <button
                className="px-2 py-1 rounded border border-gray-300 text-xs hover:bg-gray-100 disabled:opacity-40"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >Next ›</button>
              <button
                className="px-2 py-1 rounded border border-gray-300 text-xs hover:bg-gray-100 disabled:opacity-40"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >»</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Notify Modal ───────────────────────────────────────────────────── */}
      {/* ── Notify Modal ───────────────────────────────────────────────────── */}
      {notifyModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div
            className="rounded-xl shadow-2xl w-full relative"
            style={{
              maxWidth: "500px",
              backgroundColor: "#FFFFFF",
              padding: "24px",
            }}
          >
            <h2
              className="text-xl font-bold mb-6 text-center"
              style={{ color: "#111827" }}
            >
              Send Reminder
            </h2>

            <div className="space-y-4">
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: "#374151" }}
                >
                  Task Name
                </label>
                <input
                  type="text"
                  value={notifyModal.subtask_title}
                  readOnly
                  className="w-full p-2 rounded-md border"
                  style={{
                    borderColor: "#D1D5DB",
                    backgroundColor: "#F9FAFB",
                    color: "#1F2937",
                  }}
                />
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: "#374151" }}
                >
                  Assigned User
                </label>
                <input
                  type="text"
                  value={
                    notifyModal.assigned_to_name ||
                    notifyModal.assigned_to_username ||
                    "Unassigned"
                  }
                  readOnly
                  className="w-full p-2 rounded-md border"
                  style={{
                    borderColor: "#D1D5DB",
                    backgroundColor: "#F9FAFB",
                    color: "#1F2937",
                  }}
                />
              </div>

              <div
                className="p-3 rounded-md text-sm"
                style={{
                  backgroundColor: "#F9FAFB",
                  border: "1px solid #D1D5DB",
                  color: "#6B7280",
                }}
              >
                A push notification reminder will be sent to the assigned user for this overdue task.
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setNotifyModal(null)}
                  className="px-5 py-2 rounded-md font-semibold transition-colors duration-200"
                  style={{
                    backgroundColor: "#E5E7EB",
                    color: "#374151",
                  }}
                >
                  Close
                </button>

                <button
                  onClick={handleNotify}
                  disabled={notifying}
                  className="px-5 py-2 rounded-md font-semibold transition-colors duration-200 disabled:opacity-50"
                  style={{
                    backgroundColor: "#2563EB",
                    color: "#FFFFFF",
                  }}
                >
                  {notifying ? "Sending..." : "Send Reminder"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Reassign Modal ─────────────────────────────────────────────────── */}
      {reassignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}>

          <div
            className="rounded-xl shadow-2xl w-full relative"
            style={{
              maxWidth: "500px",
              backgroundColor: "#FFFFFF",
              padding: "24px",
            }}
          >
            <h2
              className="text-xl font-bold mb-6 text-center"
              style={{ color: "#111827" }}
            >
              Reassign Task
            </h2>

            <div className="space-y-4">
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: "#374151" }}
                >
                  Task Name
                </label>
                <input
                  type="text"
                  value={reassignModal.subtask_title}
                  readOnly
                  className="w-full p-2 rounded-md border"
                  style={{
                    borderColor: "#D1D5DB",
                    backgroundColor: "#F9FAFB",
                    color: "#1F2937",
                  }}
                />
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: "#374151" }}
                >
                  Select New Assignee
                </label>

                <Select
                  options={allUsers}
                  value={allUsers.find((u) => u.value === selectedUser) || null}
                  onChange={(selected) =>
                    setSelectedUser(selected ? selected.value : "")
                  }
                  placeholder="Search user..."
                  isSearchable
                  isClearable
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: "42px",
                      borderColor: "#D1D5DB",
                      backgroundColor: "#F9FAFB",
                      borderRadius: "6px",
                      boxShadow: "none",
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isFocused ? "#EFF6FF" : "#FFFFFF",
                      color: "#1F2937",
                      cursor: "pointer",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "#6B7280",
                    }),
                  }}
                />
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setReassignModal(null);
                    setSelectedUser("");
                  }}
                  className="px-5 py-2 rounded-md font-semibold transition-colors duration-200"
                  style={{
                    backgroundColor: "#E5E7EB",
                    color: "#374151",
                  }}
                >
                  Close
                </button>

                <button
                  onClick={handleReassign}
                  disabled={reassigning || !selectedUser}
                  className="px-5 py-2 rounded-md font-semibold transition-colors duration-200 disabled:opacity-50"
                  style={{
                    backgroundColor: "#2563EB",
                    color: "#FFFFFF",
                  }}
                >
                  {reassigning ? "Reassigning..." : "Reassign"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OverdueTasks;
